import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/lang";
import { Seo, ORIGIN } from "@/components/Seo";
import { PRODUCTS } from "@/content/site";
import { getLanding } from "@/content/serviceLandings";
import { Reveal, ParallaxImage } from "@/lib/motion";
import { LocaleLink as Link } from "@/i18n/routes";

export default function ProductDetail({ data, locale }) {
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const synced = useRef(false);

  useEffect(() => {
    synced.current = false;
    setLang(locale);
  }, [locale, setLang]);
  useEffect(() => {
    if (lang === locale) {
      synced.current = true;
      return;
    }
    if (synced.current) {
      const other = lang === "de" ? data.de : data.en;
      navigate(lang === "de" ? `/de/produkte/${other.slug}` : `/en/products/${other.slug}`);
    }
  }, [lang, locale, data, navigate]);

  const product = PRODUCTS.find((p) => p.id === data.id);
  if (!product) return null;
  const c = data[locale];

  const backLabel = locale === "de" ? "Alle Produkte" : "All products";
  const externalLabel =
    /gumroad/i.test(product.platform)
      ? locale === "de" ? "Auf Gumroad ansehen" : "View on Gumroad"
      : product.id === "minijob-ai"
      ? locale === "de" ? "MiniJob AI öffnen" : "Open MiniJob AI"
      : product.id === "esvan"
      ? locale === "de" ? "ESVAN öffnen" : "Open ESVAN"
      : locale === "de" ? "Auf Amazon ansehen" : "View on Amazon";
  const volumesLabel = locale === "de" ? "Bände" : "Volumes";
  const serviceLabel = locale === "de" ? "Passende Leistung" : "Related service";
  const projectLabel = locale === "de" ? "Zugehöriges Projekt" : "Related project";

  const service = getLanding(data.relatedService);
  const servicePath = service ? service[locale].path : null;
  const serviceName = service ? service[locale].h1 : null;

  const pagePath = locale === "de" ? `/de/produkte/${data.de.slug}` : `/en/products/${data.en.slug}`;
  const alternates = [
    { hrefLang: "de", href: `${ORIGIN}/de/produkte/${data.de.slug}` },
    { hrefLang: "en", href: `${ORIGIN}/en/products/${data.en.slug}` },
    { hrefLang: "x-default", href: `${ORIGIN}/de/produkte/${data.de.slug}` },
  ];

  // Structured data — only real, known properties. No price/ISBN/rating.
  const AUTHOR = { "@type": "Person", name: "Florian Teodorescu" };
  const isBook = data.schema === "BookSeries" || data.schema === "Book";
  const graph = [];
  if (data.schema === "BookSeries") {
    graph.push({
      "@type": "BookSeries",
      name: product.title,
      description: c.description,
      image: product.image,
      url: ORIGIN + pagePath,
      genre: data.genre,
      author: AUTHOR,
      hasPart: (product.volumes || []).map((v) => ({ "@type": "Book", name: v.label, url: v.link, author: AUTHOR })),
    });
  } else if (data.schema === "Book") {
    const first = (product.volumes || [])[0];
    graph.push({
      "@type": "Book",
      name: product.title,
      description: c.description,
      image: product.image,
      url: ORIGIN + pagePath,
      genre: data.genre,
      author: AUTHOR,
      ...(first ? { sameAs: first.link } : {}),
    });
  } else if (data.schema === "Course") {
    graph.push({
      "@type": "Course",
      name: product.title,
      description: c.description,
      url: ORIGIN + pagePath,
      provider: { "@type": "Organization", name: "WERKVERDUNG", url: ORIGIN },
    });
  } else if (data.schema === "SoftwareApplication") {
    graph.push({
      "@type": "SoftwareApplication",
      name: product.title,
      description: c.description,
      url: ORIGIN + pagePath,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web, Android",
    });
  }
  graph.push({
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: ORIGIN + (locale === "de" ? "/" : "/en") },
      { "@type": "ListItem", position: 2, name: locale === "de" ? "Produkte" : "Products", item: ORIGIN + (locale === "de" ? "/products" : "/en/products") },
      { "@type": "ListItem", position: 3, name: product.title, item: ORIGIN + pagePath },
    ],
  });
  const jsonLd = { "@context": "https://schema.org", "@graph": graph };

  const externalLinks = product.volumes
    ? product.volumes
    : product.link
    ? [{ label: product.title, link: product.link }]
    : [];

  return (
    <div data-testid={`product-detail-${data.id}-${locale}`}>
      <Seo
        title={c.title}
        description={c.description}
        path={pagePath}
        lang={locale}
        image={product.image}
        type="article"
        alternates={alternates}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-14 border-b border-border">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <Link to="/products" data-testid="product-back" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-stone hover:text-bronze transition-colors mb-10">
            <ArrowRight size={13} className="rotate-180" /> {backLabel}
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">— {product.platform}</span>
                <h1 className="mt-6 font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight text-ink leading-[1.0]">{c.h1}</h1>
                {isBook && (
                  <p data-testid="product-author" className="mt-4 font-mono text-[12px] uppercase tracking-widest text-bronze">
                    {locale === "de" ? "von Florian Teodorescu" : "by Florian Teodorescu"}
                  </p>
                )}
                <p className="mt-7 max-w-xl text-lg md:text-xl leading-relaxed text-stone">{c.intro}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <ParallaxImage src={product.image} alt={`${c.h1} — Cover`} className="w-full aspect-[4/5] bg-secondary" strength={20} />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-14 md:py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 space-y-12">
              {c.sections.map((s, i) => (
                <Reveal key={i}>
                  <div>
                    <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-ink">{s.h2}</h2>
                    <p className="mt-4 text-base md:text-lg leading-relaxed text-stone">{s.body}</p>
                  </div>
                </Reveal>
              ))}

              {/* External CTAs (Amazon / Gumroad / MiniJob AI) */}
              <Reveal>
                <div>
                  {product.volumes && (
                    <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-4">{volumesLabel}</p>
                  )}
                  <div className="flex flex-col gap-3">
                    {externalLinks.map((v, k) => (
                      <a
                        key={k}
                        href={v.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`product-external-${k}`}
                        className="group flex items-center justify-between gap-4 border border-border rounded-lg px-6 py-4 hover:border-bronze transition-colors"
                      >
                        <span className="flex items-center gap-4">
                          {v.cover && <img src={v.cover} alt={v.label} loading="lazy" className="h-14 w-11 object-cover rounded" />}
                          <span className="text-base text-ink">{v.label}</span>
                        </span>
                        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-bronze whitespace-nowrap">
                          {externalLabel} <ArrowUpRight size={14} />
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Sidebar internal links */}
            <aside className="lg:col-span-4 space-y-8">
              {servicePath && (
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-3">{serviceLabel}</p>
                  <Link to={servicePath} data-testid="product-related-service" className="group inline-flex items-center gap-2 font-serif text-xl text-ink hover:text-bronze transition-colors">
                    {serviceName} <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              )}
              {data.relatedProject && (
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-bronze mb-3">{projectLabel}</p>
                  <Link to={`/work/${data.relatedProject}`} data-testid="product-related-project" className="group inline-flex items-center gap-2 font-serif text-xl text-ink hover:text-bronze transition-colors">
                    {locale === "de" ? "Case Study ansehen" : "View case study"} <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              )}
              <div>
                <Link to="/products" data-testid="product-back-2" className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-stone hover:text-bronze transition-colors">
                  <ArrowRight size={13} className="rotate-180" /> {backLabel}
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
